import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC } from 'react';

import { fetchCollectionInfo } from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { CollectionInfo } from '@/shared/models/collection';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { nullableData } from '@/shared/utils/http';
import { TRACER_NAME_BUILDER, getSpan } from '@/shared/utils/tracer';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import { CollectionInput, Row } from './Row';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  fullWidth: boolean;
  action: BaseAction;
  actionVariant?: ActionVariant;
  collectionIds: CollectionInput[];
}

type Props = BuilderInput & {
  locale?: string;
  collections: CollectionInfo[];
};

export const Collections: FC<Props> = ({
  componentLinkId,
  title,
  description,
  fullWidth,
  action,
  actionVariant = ActionVariant.NONE,
  collectionIds = [],
  collections = [],
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  return (
    <div
      {...attributes}
      data-testid="collections"
      id={componentLinkId}
      className={cn(builderStyles, spacingStyles, backgroundColorClassName)}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={action}
        actionVariant={actionVariant}
        locale={blockLocale}
        fullWidth={fullWidth}
        className="u-wrapper"
        headerClassName={cn({
          'u-m-b-xl u-m-b-lg-xxl-6': collectionIds && collectionIds.length > 0,
        })}
      >
        <Row
          locale={blockLocale}
          collectionIds={collectionIds}
          collections={collections}
        />
      </ComponentHeader>
    </div>
  );
};

async function fetchCollectionItems(
  locale: string,
  collectionIds: CollectionInput[]
): Promise<CollectionInfo[]> {
  if (collectionIds.length === 0) return [];

  const ids = collectionIds.map((item) => item.collectionId);
  const collectionInfos = (
    await Promise.all(
      ids.map(async (id) => {
        const eitherCollection = await fetchCollectionInfo(locale, id);
        return pipe(eitherCollection, nullableData);
      })
    )
  ).filter((item): item is CollectionInfo => item !== null);

  return collectionInfos;
}

export function dataFetcher(locale: string) {
  return async ({ collectionIds = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'Collections.dataFetcher');
    const collections = await fetchCollectionItems(locale, collectionIds);
    span.end();
    return {
      locale,
      collections,
    };
  };
}

export const NAME = 'Collections';

export function componentRegister() {
  Builder.registerComponent(Collections, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'collectionIds',
        type: 'list',
        friendlyName: 'Collections',
        subFields: [
          {
            name: 'collectionId',
            friendlyName: 'Collection ID',
            type: 'number',
          },
        ],
      },
    ],
    noWrap: true,
  });

  Builder.registerComponent(Collections, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'collectionIds',
        type: 'list',
        friendlyName: 'Collections',
        subFields: [
          {
            name: 'collectionId',
            friendlyName: 'Collection ID',
            type: 'number',
          },
        ],
      },
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
